import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IMessage } from '../../../Model/Message/IMessage.js'
import type { IBlockingRequest } from '../../../Model/Message/IBlockingRequest.js'
import type { ClientMessage } from '../../Epic/Websocket/ClientMessages.js'
import type { SystemMessages } from '../../../Generated/SystemMessages.ts'

export interface IWebsocketConfig {
  url: string
  protocolVersion?: string
  reconnectionTimeout: number
}

/*
 * Is the backend reachable at all? Deliberately decoupled from the websocket:
 * the socket is one reporter among several (failed requests and the browser's own
 * offline event are others), and several subsystems want to react to recovery.
 *
 * Transitions are asymmetric on purpose:
 * - online -> offline is REPORTED by whatever failed (reportBackendUnreachable).
 * - offline -> online is PROVEN by a health probe (connectivityProbeSucceeded) or
 *   by a genuine socket open (connectionReady).
 * There is deliberately no polling while online — see connectivityEpic.
 */
export interface IConnectivityState {
  status: 'online' | 'offline'
  /* A health probe is in flight (drives the banner's disabled button). */
  probing: boolean
  /* Consecutive failed probes; drives the probe backoff. Reset on success. */
  failedProbes: number
  /* Epoch ms of the last finished probe, success or failure. */
  lastCheckedAt?: number
}

export interface IApplicationState {
  messages: IMessage[]
  openRequests: string[]
  blockingRequests: IBlockingRequest[]
  compactMode: boolean
  sidebarOpen: boolean
  hoverOverAvatar: boolean
  systemMessages: SystemMessages[]
  connectivity: IConnectivityState
  /* Config of the last connectToServer, kept so the socket can be re-armed later
   * without the mounting component having to dispatch again. */
  websocketConfig?: IWebsocketConfig
  /* True once a socket has genuinely opened at least once. Distinguishes a first
   * connect from a reconnect, which is what decides whether subscriptions need
   * re-establishing — see resyncOnReconnectEpic. */
  hasConnectedBefore: boolean
  /* The socket was terminated because the backend rejected our credentials (close
   * code 3000). That is an auth failure, NOT an outage: it must not touch
   * connectivity, and it clears when the token is refreshed. */
  websocketUnauthorized: boolean
}

// Load compactMode from localStorage if available
const getInitialCompactMode = (): boolean => {
  try {
    const storedCompactMode = localStorage.getItem('compactMode')
    return storedCompactMode !== 'false'
  } catch (error) {
    console.error('Error reading compactMode from localStorage:', error)
    return false
  }
}

const initialState: IApplicationState = {
  messages: [],
  openRequests: [],
  blockingRequests: [],
  compactMode: getInitialCompactMode(),
  sidebarOpen: false,
  hoverOverAvatar: false,
  systemMessages: [],
  connectivity: {
    status: 'online',
    probing: false,
    failedProbes: 0
  },
  websocketConfig: undefined,
  hasConnectedBefore: false,
  websocketUnauthorized: false
}

export const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    loadSystemMessages: (state, action: PayloadAction<{ all: boolean }>) => {
      state.openRequests.push(action.type)
      state.systemMessages = []
    },
    loadSystemMessagesSuccess: (state, action: PayloadAction<SystemMessages[]>) => {
      state.systemMessages = action.payload
      state.openRequests = state.openRequests.filter((request) => request !== applicationSlice.actions.loadSystemMessages.type)
    },
    loadSystemMessagesFailure: (state, action: PayloadAction<Error>) => {
      console.error(action.payload)
      state.openRequests = state.openRequests.filter((request) => request !== applicationSlice.actions.loadSystemMessages.type)
    },
    addMessage: (state, action: PayloadAction<IMessage>) => {
      state.messages.push(action.payload)
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter((msg) => msg.id !== action.payload)
    },
    addBlockingRequest: (state, action: PayloadAction<IBlockingRequest>) => {
      /* id must be unique */
      if (state.blockingRequests.find((x) => x.id == action.payload.id)) return
      console.log('adding request', action.payload)
      state.blockingRequests.push(action.payload)
    },
    removeBlockingRequest: (state, action: PayloadAction<string>) => {
      console.log('removing request', action.payload)
      state.blockingRequests = state.blockingRequests.filter((x) => x.id != action.payload)
    },
    reset: () => {
      return initialState
    },
    connectToServer: (state, action: PayloadAction<IWebsocketConfig>) => {
      console.info('Connecting to websocket server', action)
      /* Remembered so the socket can be re-armed after an outage or a token
       * refresh — see reconnectWebsocketEpic. */
      state.websocketConfig = action.payload
    },
    reconnectToServer: (state, action: PayloadAction<IWebsocketConfig>) => {
      console.info('Reconnect to server', action)
    },
    disconnectFromServer: (state, action: PayloadAction<string>) => {
      console.info('Disconnect from server', action)
    },
    connectionReady: (state, action: PayloadAction<{ isReconnect: boolean }>) => {
      console.info('Connection Ready', action)
      /* A genuine socket open proves the backend is reachable, so it doubles as
       * connectivity evidence. It deliberately does NOT emit
       * connectivityProbeSucceeded: that action re-arms the socket, and a socket
       * announcing its own open would loop forever. */
      state.connectivity.status = 'online'
      state.connectivity.probing = false
      state.hasConnectedBefore = true
      state.websocketUnauthorized = false
    },
    /* Reported by any subsystem that just failed to reach the backend (websocket
     * out of attempts, a failed request, the browser going offline). */
    reportBackendUnreachable: (state) => {
      console.info('Backend unreachable')
      state.connectivity.status = 'offline'
      /* Any probe that was in flight when we last went online was cancelled with
       * it, so start this outage with a clean flag rather than a stuck one. */
      state.connectivity.probing = false
    },
    /* User pressed "check now", or the tab/network came back. Epic-only trigger. */
    checkConnectivityNow: () => {
      console.info('Connectivity check requested')
    },
    connectivityProbeStarted: (state) => {
      state.connectivity.probing = true
    },
    connectivityProbeSucceeded: (state) => {
      console.info('Backend reachable again')
      state.connectivity.status = 'online'
      state.connectivity.probing = false
      state.connectivity.failedProbes = 0
      state.connectivity.lastCheckedAt = Date.now()
    },
    connectivityProbeFailed: (state) => {
      state.connectivity.probing = false
      state.connectivity.failedProbes += 1
      state.connectivity.lastCheckedAt = Date.now()
    },
    /* Close code 3000: the backend rejected our credentials. Auth problem, not an
     * outage — kept out of connectivity on purpose. */
    websocketUnauthorized: (state) => {
      console.info('Websocket terminated: not authorized')
      state.websocketUnauthorized = true
    },
    receivedWebSocketMessage: (state, action: PayloadAction<unknown>) => {
      console.info('received web socket message', action)
    },
    sendWebSocketMessage: (state, action: PayloadAction<ClientMessage>) => {
      console.info('sending socket message', action)
    },
    sendWebSocketMessageDone: (state, action: PayloadAction<unknown>) => {
      console.info('Sending websocketMessageDone', action)
    },
    toggleCompactMode: (state) => {
      state.compactMode = !state.compactMode
      try {
        localStorage.setItem('compactMode', state.compactMode.toString())
      } catch (error) {
        console.error('Error saving compactMode to localStorage:', error)
      }
    },
    setCompactMode: (state, action: PayloadAction<boolean>) => {
      state.compactMode = action.payload
      try {
        localStorage.setItem('compactMode', state.compactMode.toString())
      } catch (error) {
        console.error('Error saving compactMode to localStorage:', error)
      }
    },
    toggleSidebarOpen: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    setHoverOverAvatar: (state, action: PayloadAction<boolean>) => {
      state.hoverOverAvatar = action.payload
    },
    toggleHoverOverAvatar: (state) => {
      state.hoverOverAvatar = !state.hoverOverAvatar
    }
  }
})

export const {
  addMessage,
  removeMessage,
  addBlockingRequest,
  removeBlockingRequest,
  loadSystemMessages,
  connectToServer,
  reconnectToServer,
  disconnectFromServer,
  connectionReady,
  reportBackendUnreachable,
  checkConnectivityNow,
  connectivityProbeStarted,
  connectivityProbeSucceeded,
  connectivityProbeFailed,
  websocketUnauthorized,
  receivedWebSocketMessage,
  sendWebSocketMessage,
  sendWebSocketMessageDone,
  toggleCompactMode,
  setCompactMode,
  toggleSidebarOpen,
  setSidebarOpen,
  setHoverOverAvatar,
  toggleHoverOverAvatar
} = applicationSlice.actions

// Selectors
export const selectCompactMode = (state: { application: IApplicationState }) => state.application.compactMode
export const selectSidebarOpen = (state: { application: IApplicationState }) => state.application.sidebarOpen
export const selectHoverOverAvatar = (state: { application: IApplicationState }) => state.application.hoverOverAvatar
export const selectConnectivity = (state: { application: IApplicationState }) => state.application.connectivity

export default applicationSlice.reducer
